import React, {useEffect} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {ContentHotel} from '@findhotel/sapi/dist/types/packages/core/src/types/types'
import {BottomCTA} from 'components/Accommodation/Offers/BottomCTA'
import {scrollToSection} from 'components/ScrollSpy'
import {useSeoHotelDescription} from 'hooks/useSeoHotelDescription/useSeoHotelDescription'
import useShowOverlay from 'hooks/useShowOverlay/useShowOverlay'
import {AccommodationPageNavSections} from 'modules/accommodation/types'
import {getHotelDescriptionOverlay} from 'modules/overlay/selectors'
import {hideOverlay} from 'modules/overlay/slice'
import {OverlayType} from 'modules/overlay/types'

import {BottomSheet} from '@vio/atlas/BottomSheet'
import {Button} from '@vio/atlas/Button'
import {ButtonLink} from '@vio/atlas/ButtonLink'
import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Divider} from '@vio/atlas/Divider'
import {Box} from '@vio/atlas/helpers/Box'
import {PopUp} from '@vio/atlas/PopUp'
import {Text} from '@vio/atlas/Text'
import {useInView} from '@vio/core/src/_web/utils/browser/hooks/useInView'
import {useDispatchTeamEvent} from '@vio/core/src/analytics/hooks/useDispatchTeamEvent'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  AnalyticsContext,
  Category,
  Entity,
  HotelContext
} from '@vio/core/src/analytics/types/Events'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

import HotelPropertyInfoContent from '../HotelPropertyInfo/HotelPropertyInfoContent/HotelPropertyInfoContent'
import {generateHotelDescription} from '../utils/generateHotelDescription'

interface Props {
  hotel?: ContentHotel
  title?: React.ReactNode
  withTopDivider?: boolean
  descriptionPaddingBottom?: 's200' | 's500'
  titlePaddingBottom?: 's200' | 's300' | 's500'
  descriptionLineClamp?: number
}

const StyledText = styled(Text)`
  p {
    margin: 0;
  }
  ul {
    margin: 0;
  }
`

const ClampedHtml = styled.div<{$lineClamp: number}>`
  display: -webkit-box;
  -webkit-line-clamp: ${({$lineClamp}) => $lineClamp};
  -webkit-box-orient: vertical;
  overflow: hidden;
`

export const HotelDescriptions = ({
  hotel,
  title,
  withTopDivider = true,
  descriptionPaddingBottom = 's500',
  titlePaddingBottom = 's500',
  descriptionLineClamp
}: Props) => {
  const {ref, inView} = useInView({triggerOnce: true, threshold: 0.5})
  const dispatchTeamEvent = useDispatchTeamEvent()
  const {isMobile, isDesktop} = useDeviceLayout()
  const showOverlay = useShowOverlay()
  const {objectID: hotelId, descriptions: sapiDescriptions} = hotel || {}
  const {version} = sapiDescriptions || {}
  const seoDescription = useSeoHotelDescription(hotelId)
  const descriptions = {...sapiDescriptions, ...seoDescription}
  const {poi: attractions} = hotel?.location || {}

  const dispatch = useDispatch()
  const isOpen = Boolean(useSelector(getHotelDescriptionOverlay))
  const onClose = () => dispatch(hideOverlay())

  const {descriptionDisplayed, shouldLimitDescription} =
    generateHotelDescription(descriptions, {
      preferHeadline: Boolean(seoDescription)
    })

  useEffect(() => {
    if (inView) {
      const hotelContext = {
        hotelId,
        isAIDescriptionEligible: version === 2
      } as HotelContext

      dispatchTeamEvent(
        trackEvent({
          category: Category.User,
          entity: Entity.HotelDetailsDescription,
          action: Action.Viewed,
          analyticsContext: {
            [AnalyticsContext.HotelContext]: hotelContext
          }
        })
      )
    }
  }, [dispatchTeamEvent, hotelId, inView, version])

  const handleShowMoreClick = () => {
    dispatchTeamEvent(
      trackEvent({
        category: Category.User,
        entity: Entity.HotelDetailsDescription,
        action: Action.Clicked
      })
    )

    showOverlay({
      type: OverlayType.HotelDescription
    })
  }

  const handleDesktopBottomCTAClick = () => {
    dispatch(hideOverlay())
    scrollToSection(AccommodationPageNavSections.Rooms, {deferred: true})
  }

  if (!descriptionDisplayed) return null

  return (
    <div ref={ref} data-id="hotelDescription">
      {isDesktop && withTopDivider && (
        <Box paddingTop="s600" paddingBottom="s600">
          <Divider />
        </Box>
      )}
      <Box
        paddingTop={isMobile ? 's700' : 's000'}
        paddingBottom={isMobile ? 's700' : 's000'}
        paddingLeft={isMobile ? 's500' : 's000'}
        paddingRight={isMobile ? 's500' : 's000'}
      >
        <Box paddingBottom={titlePaddingBottom}>
          <Text variant="titleL" tag="h2">
            {title ?? (
              <FormattedMessageWrapper
                id="propertyDescription"
                defaultMessage="Property description"
              />
            )}
          </Text>
        </Box>
        <Box paddingBottom={descriptionPaddingBottom}>
          <StyledText variant="bodyM" tag="div" dir="auto">
            {descriptionLineClamp ? (
              <ClampedHtml
                $lineClamp={descriptionLineClamp}
                dangerouslySetInnerHTML={{__html: descriptionDisplayed}}
              />
            ) : (
              <Box
                display="flex"
                flexDirection="column"
                gap="s400"
                dangerouslySetInnerHTML={{
                  __html: descriptionDisplayed
                }}
              />
            )}
          </StyledText>
        </Box>
        {shouldLimitDescription && (
          <ButtonLink
            variant="primary"
            size={isMobile ? 'md' : 'lg'}
            isCompact
            onClick={handleShowMoreClick}
          >
            <FormattedMessageWrapper id="showMore" defaultMessage="Show more" />
          </ButtonLink>
        )}

        {shouldLimitDescription && hotelId && isMobile && (
          <BottomSheet
            prerenderContent
            navBarProps={{
              centerContent: (
                <FormattedMessageWrapper
                  id="propertyDescription"
                  defaultMessage="Property description"
                />
              ),
              withDivider: true
            }}
            snapPoints={{top: 'full', middle: 0.9}}
            withDragHandler
            footerContent={
              <BottomCTA
                hotelId={hotelId}
                sourceComponent="MobileHotelViewAllPoliciesStaticOfferCTA"
              />
            }
            isOpen={isOpen}
            onClose={onClose}
            prefersReducedMotion
          >
            <Box paddingTop="s500">
              <HotelPropertyInfoContent
                attractions={attractions}
                descriptions={descriptions}
              />
            </Box>
          </BottomSheet>
        )}

        {shouldLimitDescription && hotelId && !isMobile && (
          <PopUp
            prerenderContent
            width={640}
            navBarProps={{
              centerContent: (
                <FormattedMessageWrapper
                  id="propertyDescription"
                  defaultMessage="Property description"
                />
              ),
              withDivider: true
            }}
            footer={
              <Button
                size="lg"
                fullWidth
                variant="primary"
                onClick={handleDesktopBottomCTAClick}
              >
                <FormattedMessageWrapper
                  id="viewRooms"
                  defaultMessage="View rooms"
                />
              </Button>
            }
            isOpen={isOpen}
            closeAction={onClose}
          >
            <Box paddingTop="s500">
              <HotelPropertyInfoContent
                attractions={attractions}
                descriptions={descriptions}
              />
            </Box>
          </PopUp>
        )}
      </Box>
      {isMobile && <Divider sideMargin="s500" />}
    </div>
  )
}
